import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const defaultKeySerializer = (queryKey) => {
  if (Array.isArray(queryKey)) {
    return JSON.stringify(queryKey);
  }
  return String(queryKey);
};

export const useQuery = (queryKey, queryFn, options = {}) => {
  const { enabled = true, onError } = options;

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const queryFnRef = useRef(queryFn);
  const onErrorRef = useRef(onError);

  const key = useMemo(() => defaultKeySerializer(queryKey), [queryKey]);

  useEffect(() => {
    queryFnRef.current = queryFn;
  }, [queryFn]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const runQuery = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await queryFnRef.current();
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      if (onErrorRef.current) {
        onErrorRef.current(err);
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    runQuery();
  }, [runQuery, key]);

  return {
    data,
    isLoading,
    error,
    refetch: runQuery,
  };
};
